import React from 'react';

export interface CallControlsProps {
  // Audio/Video state
  isAudioEnabled: boolean;
  isVideoEnabled: boolean;
  onToggleAudio: () => void;
  onToggleVideo: () => void;
  onSwitchCamera?: () => void;

  // Session controls
  onNext?: () => void;
  onEndCall: () => void;
  onReport: () => void;

  // User/session info
  isPremium: boolean;
  sessionTime: number; // in seconds
  timeRemaining?: number; // for free users, in seconds
  connectionState: string;

  // Connection quality
  connectionQuality?: {
    quality: 'excellent' | 'good' | 'fair' | 'poor' | 'unknown';
    icon: string;
    label: string;
    color: string;
  };

  // Disable states
  disabled?: boolean;
}

export const CallControls: React.FC<CallControlsProps> = ({
  isAudioEnabled,
  isVideoEnabled,
  onToggleAudio,
  onToggleVideo,
  onSwitchCamera,
  onNext,
  onEndCall,
  onReport,
  isPremium,
  sessionTime,
  timeRemaining,
  connectionState,
  connectionQuality,
  disabled = false,
}) => {
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const showTimeWarning = timeRemaining !== undefined && timeRemaining <= 60 && timeRemaining > 0;
  const isConnected = connectionState === 'connected';

  return (
    <div style={styles.container}>
      {/* Timer and Quality Indicator */}
      <div style={styles.infoBar}>
        {/* Session Timer */}
        <div style={styles.timer}>
          <span style={styles.timerIcon}>⏱️</span>
          <span style={styles.timerText}>{formatTime(sessionTime)}</span>
        </div>

        {/* Connection Quality */}
        {isConnected && connectionQuality && (
          <div style={styles.quality}>
            <span style={styles.qualityIcon}>{connectionQuality.icon}</span>
            <span style={{ ...styles.qualityLabel, color: connectionQuality.color }}>
              {connectionQuality.label}
            </span>
          </div>
        )}

        {/* Time Remaining Warning (Free Users) */}
        {!isPremium && timeRemaining !== undefined && timeRemaining > 0 && (
          <div
            style={{
              ...styles.timeWarning,
              ...(showTimeWarning ? styles.timeWarningUrgent : {}),
            }}
          >
            <span style={styles.warningIcon}>⏰</span>
            <span style={styles.warningText}>
              {showTimeWarning
                ? `${timeRemaining}s remaining`
                : `${formatTime(timeRemaining)} left`}
            </span>
          </div>
        )}
      </div>

      {/* Main Controls */}
      <div style={styles.controls}>
        {/* Audio Toggle */}
        <button
          onClick={onToggleAudio}
          disabled={disabled || !isConnected}
          style={{
            ...styles.controlButton,
            ...(isAudioEnabled ? styles.controlButtonActive : styles.controlButtonInactive),
            ...(disabled || !isConnected ? styles.controlButtonDisabled : {}),
          }}
          title={isAudioEnabled ? 'Mute microphone' : 'Unmute microphone'}
        >
          <span style={styles.controlIcon}>{isAudioEnabled ? '🎤' : '🔇'}</span>
        </button>

        {/* Video Toggle */}
        <button
          onClick={onToggleVideo}
          disabled={disabled || !isConnected}
          style={{
            ...styles.controlButton,
            ...(isVideoEnabled ? styles.controlButtonActive : styles.controlButtonInactive),
            ...(disabled || !isConnected ? styles.controlButtonDisabled : {}),
          }}
          title={isVideoEnabled ? 'Stop camera' : 'Start camera'}
        >
          <span style={styles.controlIcon}>{isVideoEnabled ? '📹' : '📷'}</span>
        </button>

        {/* Camera Switch (Mobile) */}
        {onSwitchCamera && (
          <button
            onClick={onSwitchCamera}
            disabled={disabled || !isConnected || !isVideoEnabled}
            style={{
              ...styles.controlButton,
              ...styles.controlButtonSecondary,
              ...(disabled || !isConnected || !isVideoEnabled
                ? styles.controlButtonDisabled
                : {}),
            }}
            title="Switch camera"
          >
            <span style={styles.controlIcon}>🔄</span>
          </button>
        )}

        {/* Next/Skip (Premium Only) */}
        {isConnected && (
          <button
            onClick={onNext}
            disabled={disabled || !isPremium}
            style={{
              ...styles.controlButton,
              ...styles.nextButton,
              ...(!isPremium ? styles.nextButtonLocked : {}),
              ...(disabled ? styles.controlButtonDisabled : {}),
            }}
            title={isPremium ? 'Skip to next match' : 'Premium feature - Upgrade to skip'}
          >
            <span style={styles.controlIcon}>⏭️</span>
            {!isPremium && <span style={styles.lockIcon}>🔒</span>}
          </button>
        )}

        {/* Report */}
        {isConnected && (
          <button
            onClick={onReport}
            disabled={disabled}
            style={{
              ...styles.controlButton,
              ...styles.reportButton,
              ...(disabled ? styles.controlButtonDisabled : {}),
            }}
            title="Report user"
          >
            <span style={styles.controlIcon}>🚨</span>
          </button>
        )}

        {/* End Call */}
        <button
          onClick={onEndCall}
          disabled={disabled}
          style={{
            ...styles.controlButton,
            ...styles.endCallButton,
            ...(disabled ? styles.controlButtonDisabled : {}),
          }}
          title="End call"
        >
          <span style={styles.controlIcon}>📞</span>
        </button>
      </div>

      {/* Premium Upgrade Prompt */}
      {!isPremium && timeRemaining !== undefined && timeRemaining <= 60 && timeRemaining > 0 && (
        <div style={styles.upgradePrompt}>
          <span style={styles.upgradeIcon}>⭐</span>
          <span style={styles.upgradeText}>
            Upgrade to Premium for unlimited session time!
          </span>
        </div>
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
    padding: '1rem',
    zIndex: 100,
  },
  infoBar: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '2rem',
    marginBottom: '1rem',
    flexWrap: 'wrap',
  },
  timer: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    color: 'white',
  },
  timerIcon: {
    fontSize: '1.2rem',
  },
  timerText: {
    fontSize: '1.1rem',
    fontWeight: '600',
    fontFamily: 'monospace',
  },
  quality: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  qualityIcon: {
    fontSize: '1.2rem',
  },
  qualityLabel: {
    fontSize: '0.95rem',
    fontWeight: '600',
  },
  timeWarning: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.5rem 1rem',
    backgroundColor: 'rgba(245, 158, 11, 0.2)',
    borderRadius: 'var(--radius-md)',
    border: '2px solid #f59e0b',
  },
  timeWarningUrgent: {
    backgroundColor: 'rgba(239, 68, 68, 0.2)',
    borderColor: '#ef4444',
    animation: 'pulse 1s ease-in-out infinite',
  },
  warningIcon: {
    fontSize: '1.1rem',
  },
  warningText: {
    color: 'white',
    fontSize: '0.9rem',
    fontWeight: '600',
  },
  controls: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '1rem',
    flexWrap: 'wrap',
  },
  controlButton: {
    width: '60px',
    height: '60px',
    borderRadius: '50%',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    position: 'relative',
  },
  controlButtonActive: {
    backgroundColor: '#3b82f6',
  },
  controlButtonInactive: {
    backgroundColor: '#6b7280',
  },
  controlButtonSecondary: {
    backgroundColor: '#374151',
  },
  controlButtonDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  nextButton: {
    backgroundColor: '#8b5cf6',
  },
  nextButtonLocked: {
    backgroundColor: '#4b5563',
  },
  reportButton: {
    backgroundColor: '#ef4444',
  },
  endCallButton: {
    backgroundColor: '#dc2626',
    width: '70px',
    height: '70px',
  },
  controlIcon: {
    fontSize: '1.8rem',
  },
  lockIcon: {
    position: 'absolute',
    top: '2px',
    right: '2px',
    fontSize: '0.9rem',
  },
  upgradePrompt: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.75rem',
    marginTop: '1rem',
    padding: '0.75rem 1.5rem',
    backgroundColor: 'rgba(251, 191, 36, 0.2)',
    borderRadius: 'var(--radius-md)',
    border: '2px solid #fbbf24',
  },
  upgradeIcon: {
    fontSize: '1.5rem',
  },
  upgradeText: {
    color: 'white',
    fontSize: '1rem',
    fontWeight: '600',
  },
};
